import React, {useEffect, useMemo, useState} from 'react';
import {FaCogs, FaDatabase, FaGift, FaLayerGroup, FaPlus, FaSearch, FaServer, FaSyncAlt, FaTimes} from 'react-icons/fa';
import TrialLock from '../components/TrialLock';
import '../styles/InstancesPage.scss';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from '../store/store';
import {fetchInstances} from '../store/slices/instancesSlice';
import axiosInstance from "../utility/axiosInstance";
import {handleAxiosError} from "../helpers/handleAxiosError";
import type {Instance} from '../store/slices/instancesSlice';
import type {Snapshot} from '../store/slices/snapshotsSlice';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useToast} from "../components/ToastContext";
import MobileInstanceDetailsModal from "../components/MobileInstanceDetails";
import DNSManagementView from '../components/DNSManagement/DNSManagementView';
import InstanceRow from "../components/InstanceRow";
import {FreeVmCard, FreeVmRow} from '../components/InstanceRow/FreeVmRow';
import FreeVmDetailsModal from '../components/FreeVmDetailsModal';
import {FreeVm, freeVmsFromOverview} from '../components/InstanceRow/freeVm';
import useFreeResourcesOverview from '../hooks/useFreeResourcesOverview';
import DatabaseDataView from '../components/DatabaseData/DatabaseDataView';
import {useConfirm} from "../components/ConfirmContext";
import imageSshUsers from '../data/imageSshUsers.json';
import SSHConnectionModal from '../components/SSHConnectionModal';
import SecurityGroupModal from '../components/SecurityGroupModal';
import ResizeInstanceModal from '../components/ResizeInstanceModal';
import UpgradeToClusterModal from '../components/UpgradeToClusterModal';

interface InstancesProps {
    collapsed?: boolean
}

interface NginxRoute {
    targetIp: string;
    port: number | string;
}

type InstanceView = 'all' | 'solo' | 'clusters' | 'managed-data' | 'automated' | 'free';

/**
 * A row of the table: a paid instance from the instances slice, or an included free VM from the free overview
 * (Milos, 2026-11-02). The two never mix: the paid tabs, handlers and slice never see a free VM, and a free VM's row
 * gets its own component and its own one action.
 */
type TableRow = { source: 'paid'; instance: Instance } | { source: 'free'; vm: FreeVm };

/** Newest first across both kinds; a date that does not parse sorts last instead of breaking the order. */
const createdTime = (row: TableRow) => {
    const time = Date.parse((row.source === 'free' ? row.vm.createdAt : row.instance.created) ?? '');
    return Number.isFinite(time) ? time : 0;
};

const isManagedDataInstance = (instance: Instance) => {
    const name = instance.name.toLowerCase();
    return ['db-', 'rdb-', 'redis-', 'qdrant-'].some(prefix => name.startsWith(prefix));
};

const InstancesPage: React.FC<InstancesProps> = ({collapsed}) => {
    const {confirm} = useConfirm();
    const {showToast} = useToast();
    const navigate = useNavigate();
    const [selectedMobileInstance, setSelectedMobileInstance] = useState<Instance | null>(null);
    // The free VM whose details are open, by operation id, so they close by themselves once it is gone.
    const [openFreeVmId, setOpenFreeVmId] = useState<string | null>(null);

    const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
    const [showRules, setShowRules] = useState(false);
    const [deletingInstances, setDeletingInstances] = useState<Set<string>>(new Set());
    const [makingPublic, setMakingPublic] = useState<string | null>(null);
    const [makingPrivate, setMakingPrivate] = useState<string | null>(null);

    const [isSSHModalOpen, setIsSSHModalOpen] = useState(false);
    const [sshDetails, setSSHDetails] = useState<{
        command: string;
        user: string;
        host: string;
        port: string;
    } | null>(null);

    const [isSecurityGroupModalOpen, setIsSecurityGroupModalOpen] = useState(false);
    const [instanceForSecurityGroup, setInstanceForSecurityGroup] = useState<Instance | null>(null);

    const [isResizeModalOpen, setIsResizeModalOpen] = useState(false);
    const [instanceForResize, setInstanceForResize] = useState<Instance | null>(null);

    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
    const [instanceForUpgrade, setInstanceForUpgrade] = useState<Instance | null>(null);

    const [searchQuery, setSearchQuery] = useState('');
    // ?view=free opens the Free tier tab (the menu's Start > Free tier link, Milos 2026-11-02).
    const location = useLocation();
    const viewFromUrl = new URLSearchParams(location.search).get('view') === 'free' ? 'free' : null;
    const [instanceView, setInstanceView] = useState<InstanceView>(viewFromUrl ?? 'all');
    useEffect(() => { if (viewFromUrl) setInstanceView(viewFromUrl); }, [viewFromUrl, location.key]);

    const handleRowClick = (instance: Instance) => {
        if (window.innerWidth <= 768) {
            setSelectedMobileInstance(instance);
        } else {
            navigate(`/category/compute/instances/${instance.id}`);
        }
    };

    const dispatch = useDispatch<AppDispatch>();

    React.useEffect(() => {
        dispatch(fetchInstances());
    }, [dispatch]);

    const instances = useSelector((state: RootState) => state.instances.data);
    const images = useSelector((state: RootState) => state.images.data);
    const flavors = useSelector((state: RootState) => state.flavors.data);
    const instancesStatus = useSelector((state: RootState) => state.instances.status);

    // The included free VMs: their own source, kept current by the socket's account refresh (no polling).
    const {overview: freeOverview, reload: reloadFree} = useFreeResourcesOverview();
    const freeVms = useMemo(() => freeVmsFromOverview(freeOverview), [freeOverview]);
    const freeTier = freeOverview?.enrolled === true;
    const [deletingFree, setDeletingFree] = useState<Set<string>>(new Set());
    const [freeDataViewId, setFreeDataViewId] = useState<number | null>(null);
    // The Free tier tab exists for every account enrolled in the free tier, where the home card shows, including one with
    // no free VM yet (its tab reads 0 and says so). If enrollment ends while the tab is selected, show everything.
    const view: InstanceView = instanceView === 'free' && !freeTier ? 'all' : instanceView;

    const instanceViews: { id: InstanceView; label: string; icon: React.ComponentType; count: number }[] = [
        { id: 'all', label: 'All instances', icon: FaServer, count: instances.length + freeVms.length },
        { id: 'solo', label: 'Solo', icon: FaServer, count: instances.filter(instance => instance.metadata?.tag === 'manual').length },
        { id: 'clusters', label: 'Clusters', icon: FaLayerGroup, count: instances.filter(instance => instance.cluster_member && !isManagedDataInstance(instance)).length },
        { id: 'managed-data', label: 'Managed data', icon: FaDatabase, count: instances.filter(isManagedDataInstance).length },
        { id: 'automated', label: 'Other automated', icon: FaCogs, count: instances.filter(instance => instance.metadata?.tag !== 'manual' && !instance.cluster_member && !isManagedDataInstance(instance)).length },
        ...(freeTier ? [{ id: 'free' as const, label: 'Free tier', icon: FaGift, count: freeVms.length }] : []),
    ];

    // Paid rows never appear under Free tier (the filter's last `return true` would put them all there).
    const instancesInView = view === 'free' ? [] : instances.filter((instance) => {
        if (view === 'solo') return instance.metadata?.tag === 'manual';
        if (view === 'clusters') return Boolean(instance.cluster_member) && !isManagedDataInstance(instance);
        if (view === 'managed-data') return isManagedDataInstance(instance);
        if (view === 'automated') return instance.metadata?.tag !== 'manual' && !instance.cluster_member && !isManagedDataInstance(instance);
        return true;
    });
    // Free VMs are placed by where they come from, never by name or tag: 'db-free' is not managed data.
    const freeVmsInView = view === 'all' || view === 'free' ? freeVms : [];

    const query = searchQuery.trim().toLowerCase();
    const matchesSearch = (name: string) => !query || name.toLowerCase().includes(query);
    const tableRows: TableRow[] = [
        ...instancesInView.filter(instance => matchesSearch(instance.name)).map((instance): TableRow => ({ source: 'paid', instance })),
        ...freeVmsInView.filter(vm => matchesSearch(vm.name)).map((vm): TableRow => ({ source: 'free', vm })),
    ].sort((a, b) => createdTime(b) - createdTime(a));

    const freeDataVm = freeDataViewId === null ? undefined : freeVms.find(vm => vm.databaseData?.id === freeDataViewId);
    const freeDataView = freeDataVm?.databaseData ? { ...freeDataVm.databaseData, name: freeDataVm.name } : null;

    const resolveImageName = (imageId: string) => {
        const image = images.find((img) => img.id === imageId);
        return image ? image.name : 'N/A';
    };

    const resolveFlavorName = (flavorId: string) => {
        const flavor = flavors.find((fl) => fl.id === flavorId);
        return flavor ? flavor.name : 'N/A';
    };

    const handleManageDNSClick = (instanceId: string) => {
        setSelectedInstanceId(instanceId);
        setShowRules(true);
    };

    const handleSecurityGroupClick = (instance: Instance) => {
        setInstanceForSecurityGroup(instance);
        setIsSecurityGroupModalOpen(true);
    };

    const handleResizeClick = (instance: Instance) => {
        setInstanceForResize(instance);
        setIsResizeModalOpen(true);
    };

    const handleUpgradeToClusterClick = (instance: Instance) => {
        setInstanceForUpgrade(instance);
        setIsUpgradeModalOpen(true);
    };

    const handleCreateSnapshotClick = (instance: Instance) => {
        navigate('/category/compute/instances/snapshots', { state: { instanceId: instance.id } });
    };

    const handleInstanceAction = async (instanceId: string, action: string, flavorId?: string) => {
        const actionLabels: Record<string, string> = {
            'START': 'Start Instance',
            'STOP': 'Shut Off Instance',
            'REBOOT_SOFT': 'Soft Reboot Instance',
            'REBOOT_HARD': 'Hard Reboot Instance',
            'CONFIRM_RESIZE': 'Confirm Resize',
            'REVERT_RESIZE': 'Revert Resize'
        };

        if (action !== 'START' && action !== 'CONFIRM_RESIZE') {
            const proceed = await confirm({
                title: actionLabels[action] || action,
                message: `Are you sure you want to perform ${actionLabels[action] || action} on this instance?`,
                confirmText: 'Proceed',
                cancelText: 'Cancel',
                type: action === 'REBOOT_HARD' || action === 'STOP' ? 'danger' : 'warning',
            });
            if (!proceed) return;
        }

        try {
            const response = await axiosInstance.post('/v1/instances/action', {
                instanceId,
                action,
                flavorId
            });
            if (response.status === 200) {
                showToast(`Action ${actionLabels[action] || action} initiated`, 'success');
                dispatch(fetchInstances());
            }
        } catch (err: unknown) {
            console.error(`Error performing action ${action}:`, err);
            showToast(`Failed to perform action ${actionLabels[action] || action}`, 'error');
        }
    };

    const handleBackToInstances = () => {
        setShowRules(false);
        setSelectedInstanceId(null);
    };

    const handleMakePrivate = async (instanceId: string, floatingIp: string) => {
        setMakingPrivate(instanceId);
        try {
            const response = await axiosInstance.post('/v1/instance/make-private', {instanceId});
            if (response.status !== 200) throw new Error('Failed to make instance private');

            dispatch(fetchInstances());
            showToast('Instance is now private!', 'success');
        } catch (err: unknown) {
            console.error("Error making instance private:", err);
            showToast('Failed to make instance private!', 'error');
        } finally {
            setMakingPrivate(null);
        }
    };

    const handleDeleteInstance = async (instanceId: string) => {
        try {
            const res = await axiosInstance.get('/v1/snapshots');
            const hasSnapshots = res.data.some((s: Snapshot) => s.instance_id === instanceId);

            if (hasSnapshots) {
                showToast('This instance has snapshots. Please delete all snapshots first before trying to delete the instance.', 'warning');
                return;
            }
        } catch (error) {
            console.error("Error checking for snapshots:", error);
        }

        const proceed = await confirm({
            title: 'Delete Instance',
            message: 'Are you sure you want to delete this instance?',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;
        setDeletingInstances(prev => new Set(prev).add(instanceId));
        try {
            await axiosInstance.delete(`/v1/instances/${instanceId}`);
            dispatch(fetchInstances());
        } catch (error: unknown) {
            console.error("Error deleting instance:", error);
            showToast('Failed to delete instance!', 'error');
        } finally {
            setDeletingInstances(prev => {
                const newSet = new Set(prev);
                newSet.delete(instanceId);
                return newSet;
            });
        }
    };

    // A free VM's one console action (Milos, 2026-11-02). The server decides what may go (the free VM only after its
    // private PostgreSQL VM, never during an unfinished agent action) and its refusal is shown in its own words.
    const handleDeleteFreeVm = async (vm: FreeVm) => {
        const proceed = await confirm({
            title: 'Delete Instance',
            message: `Delete ${vm.name} and everything on it? This cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;
        setDeletingFree(prev => new Set(prev).add(vm.operationId));
        try {
            await axiosInstance.delete(`/v1/free-resources/console/${vm.operationId}`);
        } catch (err: unknown) {
            showToast(handleAxiosError(err), 'error');
        } finally {
            setDeletingFree(prev => {
                const next = new Set(prev);
                next.delete(vm.operationId);
                return next;
            });
            // One read after the person's own action, as a paid delete refetches; the socket covers the rest.
            reloadFree();
        }
    };

    const handleMakePublicClick = async (instanceId: string) => {
        setMakingPublic(instanceId);
        try {
            const instance = instances.find((inst) => inst.id === instanceId);
            if (!instance) {
                showToast('Instance not found!', 'error');
                return;
            }

            showToast('Assigning floating IP to instance...', 'info');
            const response = await axiosInstance.post(`/v1/instance/make-public`, {instanceId});
            if (response.status === 200) {
                // make-public's workflow gives the instance its public name itself (a generated one, recorded and
                // written to its metadata). This used to also POST /v1/dns/create with "<instanceId>.<BASE_DOMAIN>",
                // a name that route always refuses (it is not a generated one), so a make-public that had worked
                // ended in "Something went wrong" (ROADMAP 94, finding b).
                dispatch(fetchInstances());
                showToast('Instance is now public! 🌐', 'success');
            } else {
                throw new Error('Failed to make instance public');
            }
        } catch (err: unknown) {
            handleAxiosError(err);
            showToast('Something went wrong while making instance public!', 'error');
        } finally {
            setMakingPublic(null);
        }
    };

    const handleCopy = async (instance: Instance, floatingIp: string) => {
        try {
            const fIp =
                instance.metadata?.floatinIp || // legacy typo fallback
                instance.metadata?.floatingIp ||
                instance.addresses?.['public']?.find(
                    a => a["OS-EXT-IPS:type"] === "floating"
                )?.addr;

            if (!fIp) {
                showToast('Instance does not have a floating IP yet!', 'error');
                return;
            }

            const response = await axiosInstance.get(`/v1/instances/${instance.id}/access`);
            const route = response.data?.access;
            
            // Try to get port and host from metadata first (new JIT flow)
            const metadataPort = instance.metadata?.forwardedSSHPort;
            const metadataHost = instance.metadata?.forwardedSSHHost;
            const finalPort = metadataPort || (route ? String(route.port) : null);

            if (!finalPort) {
                showToast('No port mapping found yet. Try again in a few seconds.', 'warning');
                return;
            }

            // 🔥 Use Redux refresh instead of axios.get + find
            let dns = instance.dns || "";

            if (!dns || !dns.includes(":")) {
                await dispatch(fetchInstances());

                // Get fresh instance from Redux store (NOT axios)
                const freshInstances = (await import('../store/store')).store.getState().instances.data;
                const freshInstance = freshInstances.find((i: Instance) => i.id === instance.id);

                dns = freshInstance?.dns || "";
            }

            const host = metadataHost || dns.split(":")[1] || "redu-cloud.com"; // Fallback to redu-cloud.com if no DNS
            
            const imageName = resolveImageName(instance.image_id);
            const sshUser =
                (imageSshUsers as Record<string, string>)[imageName] || 'ubuntu';

            const sshCommand = `ssh -p ${finalPort} ${sshUser}@${host}`;

            setSSHDetails({
                command: sshCommand,
                user: sshUser,
                host: host,
                port: String(finalPort)
            });
            setIsSSHModalOpen(true);
        } catch (error: unknown) {
            const message = handleAxiosError(error);
            showToast(message, 'error');
        }
    };

    const handleActualSSHCopy = async () => {
        if (sshDetails) {
            await navigator.clipboard.writeText(sshDetails.command);
            showToast('Copied to clipboard!', 'info');
            //setIsSSHModalOpen(false);
        }
    };

    const isLoadingInstances = instancesStatus === 'loading';
    // The paid list's first load. Free rows already read stay visible above it, and Free tier never waits for it.
    const showLoading = view !== 'free' && isLoadingInstances && instances.length === 0;
    const freeRowProps = (vm: FreeVm) => ({
        vm,
        deleting: deletingFree.has(vm.operationId),
        onDelete: handleDeleteFreeVm,
        onViewData: (shown: FreeVm) => setFreeDataViewId(shown.databaseData?.id ?? null),
        onOpen: (shown: FreeVm) => setOpenFreeVmId(shown.operationId),
    });
    const emptyState = (mobile: boolean) => {
        if (query) return mobile ? <p>{`No instances matching "${searchQuery.trim()}"`}</p> : `No instances matching "${searchQuery.trim()}"`;
        if (view === 'free') return <div className="resource-empty-state"><strong>No free VMs yet</strong></div>;
        return (
            <div className="resource-empty-state">
                <strong>No virtual machines yet</strong>
                <span>Launch a blank virtual machine, or start from the deployment launcher to choose a managed resource.</span>
                <span className="resource-empty-state__actions">
                    <TrialLock locked={false} paidResource><Link to="/category/compute/instances/create">Launch virtual machine</Link></TrialLock>
                    <TrialLock locked={false} paidResource><Link to="/deploy">Explore resources</Link></TrialLock>
                </span>
            </div>
        );
    };

    return (
        <div className="keypairs-page">
            {!showRules && !freeDataView && (
                <div className="page-header">
                    <p className="page-subtitle">
                        Manage your virtual machines and cloud resources
                    </p>
                    <a href="https://docs.redu.cloud/Guides/compute-guide#2-manage-instances" target="_blank"
                       rel="noopener noreferrer" className="docs-link">
                        Documentation →
                    </a>
                </div>
            )}


            {freeDataView ? (
                // The private PostgreSQL VM's data, in place of the list: the same view and props the free VM card used.
                <DatabaseDataView db={freeDataView} onBack={() => setFreeDataViewId(null)} onRefreshDatabase={reloadFree}/>
            ) : showRules && selectedInstanceId ? (
                <DNSManagementView
                    instanceId={selectedInstanceId}
                    instanceDns={instances.find(i => i.id === selectedInstanceId)?.dns || ''}
                    instances={instances}
                    onBack={handleBackToInstances}
                />
            ) : (
                <>
                    <TrialLock locked={false} paidResource>
                    <button
                        className="create-button"
                        onClick={() => navigate('/category/compute/instances/create')}
                        title="Launch New Instance"
                    >
                        <FaPlus/>
                        <span>Launch Instance</span>
                    </button>
                    </TrialLock>

                    <div className="instance-search-bar">
                        <FaSearch className="instance-search-icon" />
                        <input
                            type="text"
                            className="instance-search-input"
                            placeholder="Search by name..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button
                                className="instance-search-clear"
                                onClick={() => setSearchQuery('')}
                                aria-label="Clear search"
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>

                    <div className="instance-view-tabs" role="tablist" aria-label="Filter instances by resource type">
                        {instanceViews.map(({ id, label, icon, count }) => (
                            <button
                                type="button"
                                key={id}
                                role="tab"
                                aria-selected={view === id}
                                className={`instance-view-tab instance-view-tab--${id}${view === id ? ' is-active' : ''}`}
                                onClick={() => setInstanceView(id)}
                            >
                                {React.createElement(icon)}
                                <span>{label}</span>
                                <small>{count}</small>
                            </button>
                        ))}
                    </div>

                    <div className={`instance-resource-view instance-resource-view--${view}`}>
                    <div className="desktop-view">
                        <div className="table-container">
                            <table className="keypairs-table">
                                <thead>
                                <tr>
                                    {[
                                        {field: 'Name', tooltip: 'Instance name'},
                                        {field: 'Status', tooltip: 'Current status'},
                                        {field: 'Image', tooltip: 'Base image'},
                                        {field: 'Flavor', tooltip: 'Instance flavor'},
                                        {field: 'Created', tooltip: 'Creation date'},
                                        {field: 'Updated', tooltip: 'Last updated'},
                                        {field: 'Uptime', tooltip: 'Time since last start'},
                                        {field: 'State', tooltip: 'Power state'},
                                        {field: 'Task', tooltip: 'Current task'},
                                        {field: 'Actions', tooltip: 'Manage instance'},
                                    ].map(({field, tooltip}) => (
                                        <th
                                            key={field}
                                            data-tooltip={tooltip}
                                        >
                                            {field}
                                        </th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {tableRows.map((row) => row.source === 'free' ? (
                                    <FreeVmRow key={`free-${row.vm.operationId}`} {...freeRowProps(row.vm)}/>
                                ) : (
                                    <InstanceRow
                                        key={row.instance.id}
                                        instance={row.instance}
                                        handleManageDNSClick={handleManageDNSClick}
                                        handleSecurityGroupClick={handleSecurityGroupClick}
                                        handleCreateSnapshotClick={handleCreateSnapshotClick}
                                        handleMakePublicClick={handleMakePublicClick}
                                        handleMakePrivate={handleMakePrivate}
                                        handleDeleteInstance={handleDeleteInstance}
                                        handleCopy={handleCopy}
                                        handleRowClick={handleRowClick}
                                        handleInstanceAction={handleInstanceAction}
                                        handleResizeClick={handleResizeClick}
                                        handleUpgradeToClusterClick={handleUpgradeToClusterClick}
                                        resolveImageName={resolveImageName}
                                        resolveFlavorName={resolveFlavorName}
                                        deletingInstances={deletingInstances}
                                        makingPublic={makingPublic}
                                        makingPrivate={makingPrivate}
                                    />
                                ))}
                                {showLoading ? (
                                    <tr>
                                        <td colSpan={10} className="loading-state">
                                            <FaSyncAlt className="spin"/> Loading instances...
                                        </td>
                                    </tr>
                                ) : tableRows.length === 0 && (
                                    <tr>
                                        <td colSpan={10} className="empty-state">
                                            {emptyState(false)}
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>


                    <div className="mobile-view">
                        {tableRows.length > 0 && (
                            <div className="cards-container">
                                {tableRows.map((row) => row.source === 'free' ? (
                                    <FreeVmCard key={`free-${row.vm.operationId}`} {...freeRowProps(row.vm)}/>
                                ) : (
                                    <div key={row.instance.id} className={`keypair-card ${row.instance.metadata?.tag !== 'manual' ? 'non-manual-instance' : ''}`}
                                         onClick={() => handleRowClick(row.instance)}>
                                        <InstanceRow
                                            instance={row.instance}
                                            handleManageDNSClick={handleManageDNSClick}
                                            handleSecurityGroupClick={handleSecurityGroupClick}
                                            handleCreateSnapshotClick={handleCreateSnapshotClick}
                                            handleMakePublicClick={handleMakePublicClick}
                                            handleMakePrivate={handleMakePrivate}
                                            handleDeleteInstance={handleDeleteInstance}
                                            handleCopy={handleCopy}
                                            handleRowClick={handleRowClick}
                                            handleInstanceAction={handleInstanceAction}
                                            handleResizeClick={handleResizeClick}
                                            handleUpgradeToClusterClick={handleUpgradeToClusterClick}
                                            resolveImageName={resolveImageName}
                                            resolveFlavorName={resolveFlavorName}
                                            deletingInstances={deletingInstances}
                                            makingPublic={makingPublic}
                                            makingPrivate={makingPrivate}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                        {showLoading ? (
                            <div className="loading-state">
                                <FaSyncAlt className="spin"/>
                                <p>Loading instances...</p>
                            </div>
                        ) : tableRows.length === 0 && (
                            <div className="empty-state">
                                {emptyState(true)}
                            </div>
                        )}
                    </div>
                    </div>
                </>
            )}


            <FreeVmDetailsModal vm={freeVms.find(vm => vm.operationId === openFreeVmId) ?? null} onClose={() => setOpenFreeVmId(null)}/>

            {selectedMobileInstance && (
                <MobileInstanceDetailsModal
                    open={!!selectedMobileInstance}
                    onClose={() => setSelectedMobileInstance(null)}
                    instance={selectedMobileInstance}
                />
            )}


            {sshDetails && (
                <SSHConnectionModal
                    isOpen={isSSHModalOpen}
                    onClose={() => setIsSSHModalOpen(false)}
                    sshCommand={sshDetails.command}
                    sshUser={sshDetails.user}
                    host={sshDetails.host}
                    port={sshDetails.port}
                    onCopy={handleActualSSHCopy}
                />
            )}

            <SecurityGroupModal
                isOpen={isSecurityGroupModalOpen}
                onClose={() => setIsSecurityGroupModalOpen(false)}
                instance={instanceForSecurityGroup}
                onUpdated={() => dispatch(fetchInstances())}
            />

            <ResizeInstanceModal
                isOpen={isResizeModalOpen}
                onClose={() => setIsResizeModalOpen(false)}
                instance={instanceForResize}
                onResizeStarted={() => dispatch(fetchInstances())}
            />

            <UpgradeToClusterModal
                isOpen={isUpgradeModalOpen}
                onClose={() => setIsUpgradeModalOpen(false)}
                instance={instanceForUpgrade}
                onUpgraded={() => navigate('/category/compute/clusters')}
            />

        </div>
    );
};

export default InstancesPage;
